/*
  Beacon Sound: one small audio engine for the whole site, plus the "music collection" cover flow.

  The cover flow follows jh3y's CodePen "Infinite Cover Flow w/ GSAP" (https://codepen.io/jh3y/pen/WNRvqJP;
  public CodePen pens are MIT licensed): the same looping timeline (pan, rotateY, scale, z, zIndex), drag,
  click-to-centre and arrow keys. Differences, on purpose: no ScrollTrigger pin (the page never hijacks scroll),
  GSAP core only, our own pointer drag, and a play/pause control, because this one is a music player.

  Sound is OFF until the visitor presses play. Nothing is downloaded before that (preload "none").
  If the visitor turned sound on, the next page tries to carry on from the same second; if the browser refuses
  to start audio by itself, a small "Resume" pill is shown instead.
*/
(function () {
  'use strict';

  var dataEl = document.getElementById('beacon-sound-data');
  if (!dataEl) return;
  var tracks;
  try { tracks = JSON.parse(dataEl.textContent); } catch (e) { return; }
  if (!tracks || !tracks.length) return;

  var N = tracks.length;
  var KEY = 'bcn.sound.v1';
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* ---------- engine ---------- */
  var audio = null;
  var cur = 0;
  var playing = false;
  var resumable = false;
  var lingering = false;   /* the pill stays a few seconds after a pause, so it can be resumed */
  var lingerTimer = null;
  var pendingSeek = 0;
  var errors = 0;
  var fadeTimer = null;
  var saveTimer = null;
  var listeners = [];

  function emit(kind) { for (var i = 0; i < listeners.length; i++) listeners[i](kind || 'state'); }
  function on(fn) { listeners.push(fn); }

  function readState() {
    try { return JSON.parse(window.localStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }
  function saveState() {
    try {
      window.localStorage.setItem(KEY, JSON.stringify({ on: playing || resumable, i: cur, t: audio ? audio.currentTime : 0 }));
    } catch (e) { /* storage can be blocked; the player still works */ }
  }
  function saveSoon() {
    if (saveTimer) return;
    saveTimer = setTimeout(function () { saveTimer = null; saveState(); }, 2500);
  }

  function ensureAudio() {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'none';
    audio.addEventListener('ended', function () { goTo(cur + 1, true); });
    audio.addEventListener('timeupdate', function () { emit('time'); saveSoon(); });
    audio.addEventListener('loadedmetadata', function () {
      if (pendingSeek && isFinite(audio.duration) && pendingSeek < audio.duration - 2) audio.currentTime = pendingSeek;
      pendingSeek = 0;
      emit('time');
    });
    audio.addEventListener('play', function () { playing = true; resumable = false; lingering = false; emit(); });
    audio.addEventListener('pause', function () {
      if (!audio.ended) {
        playing = false;
        lingering = true;
        clearTimeout(lingerTimer);
        lingerTimer = setTimeout(function () { lingering = false; renderPill(); }, 6000);
        emit();
        saveState();
      }
    });
    audio.addEventListener('error', function () {
      errors += 1;
      if (errors <= N) { goTo(cur + 1, playing); } else { playing = false; emit(); }
    });
    return audio;
  }

  function fadeTo(v, ms, done) {
    if (!audio) return;
    clearInterval(fadeTimer);
    if (reduce || ms <= 0) { audio.volume = v; if (done) done(); return; }
    var from = audio.volume;
    var t0 = Date.now();
    fadeTimer = setInterval(function () {
      var k = Math.min(1, (Date.now() - t0) / ms);
      audio.volume = from + (v - from) * k;
      if (k >= 1) { clearInterval(fadeTimer); if (done) done(); }
    }, 30);
  }

  function mediaSession() {
    if (!('mediaSession' in navigator) || typeof window.MediaMetadata !== 'function') return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: tracks[cur].t,
        artist: 'Beacon Home Store',
        album: 'Beacon sound',
        artwork: [{ src: tracks[cur].c, sizes: '768x768', type: 'image/webp' }]
      });
      navigator.mediaSession.setActionHandler('play', function () { play(); });
      navigator.mediaSession.setActionHandler('pause', function () { pause(); });
      navigator.mediaSession.setActionHandler('nexttrack', function () { goTo(cur + 1, true); });
      navigator.mediaSession.setActionHandler('previoustrack', function () { goTo(cur - 1, true); });
    } catch (e) { /* optional feature */ }
  }

  function setTrack(i) {
    cur = ((i % N) + N) % N;
    var a = ensureAudio();
    if (a.getAttribute('data-i') !== String(cur)) {
      a.setAttribute('data-i', String(cur));
      a.src = tracks[cur].a;
    }
    mediaSession();
  }

  function play(i) {
    if (typeof i === 'number') setTrack(i);
    else if (!audio || !audio.getAttribute('data-i')) setTrack(cur);
    var a = ensureAudio();
    clearInterval(fadeTimer);
    a.volume = reduce ? 1 : 0;
    var p = a.play();
    if (p && p.then) {
      p.then(function () { fadeTo(1, 500); }).catch(function () {
        playing = false; resumable = true; a.volume = 1; emit(); saveState();
      });
    } else { fadeTo(1, 500); }
  }

  function pause() {
    if (!audio || audio.paused) return;
    fadeTo(0, 300, function () { audio.pause(); audio.volume = 1; });
  }

  function toggle() { if (playing) pause(); else play(); }

  /* Move to track i. If something is playing (or keepPlaying), the new track plays too. */
  function goTo(i, keepPlaying) {
    var was = playing || !!keepPlaying;
    setTrack(i);
    if (was) play();
    emit('go');
  }

  /* The cover flow reports its centred track here once it has settled. */
  function select(i) {
    i = ((i % N) + N) % N;
    if (i === cur && audio && audio.getAttribute('data-i') === String(cur)) return;
    goTo(i, false);
  }

  window.BeaconSound = {
    toggle: toggle, play: play, pause: pause, select: select, next: function () { goTo(cur + 1, true); },
    state: function () { return { index: cur, playing: playing, resumable: resumable }; }
  };

  /* ---------- toggle buttons placed anywhere in the content ---------- */
  var toggles = [].slice.call(document.querySelectorAll('[data-beacon-sound-toggle]'));
  toggles.forEach(function (b) { b.hidden = false; });
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-beacon-sound-toggle]') : null;
    if (b) { e.preventDefault(); toggle(); }
  });
  function renderToggles() {
    toggles.forEach(function (b) {
      var label = playing ? (b.getAttribute('data-label-pause') || 'Pause') : (b.getAttribute('data-label-play') || 'Listen');
      b.textContent = label;
      b.setAttribute('aria-pressed', playing ? 'true' : 'false');
    });
  }

  /* ---------- "paired with this lamp" bands (product pages): one button per track ---------- */
  var pairBtns = [].slice.call(document.querySelectorAll('[data-beacon-sound-track]'));
  pairBtns.forEach(function (b) {
    var i = parseInt(b.getAttribute('data-beacon-sound-track'), 10);
    var band = b.closest ? b.closest('[data-bpr]') : null;
    if (band && tracks[i]) {
      var img = band.querySelector('[data-bpr-cover]');
      var ttl = band.querySelector('[data-bpr-title]');
      if (img) { img.src = tracks[i].c; img.hidden = false; }
      if (ttl) ttl.textContent = tracks[i].t;
    }
    b.hidden = false;
  });
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-beacon-sound-track]') : null;
    if (!b) return;
    e.preventDefault();
    var i = parseInt(b.getAttribute('data-beacon-sound-track'), 10);
    if (playing && cur === i) pause(); else play(i);
  });
  function renderPairs() {
    pairBtns.forEach(function (b) {
      var on = playing && cur === parseInt(b.getAttribute('data-beacon-sound-track'), 10);
      b.textContent = on ? 'Pause' : 'Listen';
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /* ---------- mini pill: only when sound is on and the collection is not on screen ---------- */
  var pill = document.querySelector('[data-bsnd]');
  var flowSection = document.querySelector('[data-bcf]');
  var flowVisible = false;
  if (pill) {
    var pillBtn = pill.querySelector('[data-bsnd-toggle]');
    var pillNext = pill.querySelector('[data-bsnd-next]');
    var pillTitle = pill.querySelector('[data-bsnd-title]');
    pillBtn.addEventListener('click', function () { toggle(); });
    pillNext.addEventListener('click', function () { goTo(cur + 1, true); });
  }
  function renderPill() {
    if (!pill) return;
    var show = (playing || resumable || lingering) && !flowVisible;
    pill.hidden = !show;
    pill.classList.toggle('is-playing', playing);
    pill.querySelector('[data-bsnd-toggle]').setAttribute('aria-label', playing ? 'Pause sound' : (resumable ? 'Resume sound' : 'Play sound'));
    pill.querySelector('[data-bsnd-title]').textContent = resumable && !playing ? 'Resume sound' : tracks[cur].t;
  }
  if (flowSection && 'IntersectionObserver' in window) {
    new window.IntersectionObserver(function (entries) {
      flowVisible = entries[0].isIntersecting && entries[0].intersectionRatio > 0.3;
      renderPill();
    }, { threshold: [0, 0.3, 0.6] }).observe(flowSection);
  }

  /* ---------- cover flow ---------- */
  var flow = null;
  function fmt(s) { if (!isFinite(s) || s < 0) return '0:00'; var m = Math.floor(s / 60); var r = Math.floor(s % 60); return m + ':' + (r < 10 ? '0' : '') + r; }

  function buildFlow(root) {
    var list = root.querySelector('[data-bcf-boxes]');
    var stage = root.querySelector('[data-bcf-stage]');
    var nameEl = root.querySelector('[data-bcf-name]');
    var genreEl = root.querySelector('[data-bcf-genre]');
    var fillEl = root.querySelector('[data-bcf-fill]');
    var timeEl = root.querySelector('[data-bcf-time]');
    var durEl = root.querySelector('[data-bcf-dur]');
    var btnPrev = root.querySelector('[data-bcf-prev]');
    var btnNext = root.querySelector('[data-bcf-next]');
    var btnPlay = root.querySelector('[data-bcf-play]');
    var announce = root.querySelector('[data-bcf-announce]');
    if (!list || !stage) return null;

    tracks.forEach(function (t, i) {
      var li = document.createElement('li');
      li.className = 'bcf__box';
      li.style.setProperty('--src', 'url(' + t.c + ')');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bcf__cover';
      var img = new Image();
      img.src = t.c; img.alt = ''; img.width = 768; img.height = 768; img.decoding = 'async';
      if (i > 3) img.loading = 'lazy';
      b.appendChild(img);
      li.appendChild(b);
      list.appendChild(li);
    });
    var boxes = [].slice.call(list.children);
    var pos = 0;           /* in cycles; one cover = 1 / N */
    var settleTimer = null;
    var dragging = false, moved = false, suppress = false, sx = 0, spos = 0;
    var gsap = window.gsap;

    function snap(p) { return Math.round(p * N) / N; }
    function center(p) { return ((Math.round(p * N) % N) + N) % N; }

    var SCRUB = null;
    var ready = false;
    if (gsap) {
      var STAGGER = 0.1;
      var DURATION = 1;
      var LOOP = gsap.timeline({ paused: true, repeat: -1, ease: 'none' });
      var SHIFTS = boxes.concat(boxes, boxes);
      gsap.set(boxes, { yPercent: -50 });
      SHIFTS.forEach(function (BOX, index) {
        var tl = gsap.timeline()
          .set(BOX, { xPercent: 250, rotateY: -50, opacity: 0, scale: 0.5 })
          .to(BOX, { opacity: 1, scale: 1, duration: 0.1 }, 0)
          .to(BOX, { opacity: 0, scale: 0.5, duration: 0.1 }, 0.9)
          .fromTo(BOX, { xPercent: 250 }, { xPercent: -350, duration: 1, immediateRender: false, ease: 'power1.inOut' }, 0)
          .fromTo(BOX, { rotateY: -50 }, { rotateY: 50, immediateRender: false, duration: 1, ease: 'power4.inOut' }, 0)
          .to(BOX, { z: 100, scale: 1.25, duration: 0.1, repeat: 1, yoyo: true }, 0.4)
          .fromTo(BOX, { zIndex: 1 }, { zIndex: N, repeat: 1, yoyo: true, ease: 'none', duration: 0.5, immediateRender: false }, 0);
        LOOP.add(tl, index * STAGGER);
      });
      var CYCLE = STAGGER * N;
      var START = CYCLE + DURATION * 0.5;
      var HEAD = gsap.fromTo(LOOP, { totalTime: START }, { totalTime: '+=' + CYCLE, duration: 1, ease: 'none', repeat: -1, paused: true });
      var PLAYHEAD = { position: 0 };
      var WRAP = gsap.utils.wrap(0, HEAD.duration());
      SCRUB = gsap.to(PLAYHEAD, {
        position: 0,
        onUpdate: function () { HEAD.totalTime(WRAP(PLAYHEAD.position)); },
        paused: true,
        duration: reduce ? 0.01 : 0.25,
        ease: 'power3'
      });
      HEAD.totalTime(0);
      gsap.set(boxes, { display: 'block' });
      ready = true;
      root.classList.add('is-ready');
    } else {
      root.classList.add('bcf--static');
      boxes.forEach(function (b) { b.style.display = 'block'; });
    }

    function applyPos() {
      if (!SCRUB) return;
      SCRUB.vars.position = pos;
      SCRUB.invalidate().restart();
    }

    function hits() {
      /* Invisible covers (faded out at the ends of the loop) must not catch clicks. */
      boxes.forEach(function (b) {
        var o = ready ? Number(gsap.getProperty(b, 'opacity')) : 1;
        b.style.pointerEvents = o > 0.5 ? '' : 'none';
      });
    }

    function render() {
      var c = center(pos);
      var t = tracks[c];
      boxes.forEach(function (b, i) {
        var btn = b.firstChild;
        var isC = i === c;
        b.classList.toggle('is-center', isC);
        btn.setAttribute('aria-label', (isC ? (playing && cur === c ? 'Pause ' : 'Play ') : 'Show ') + tracks[i].t);
        if (isC) btn.removeAttribute('tabindex'); else btn.setAttribute('tabindex', '-1');
      });
      if (nameEl) nameEl.textContent = t.t;
      if (genreEl) genreEl.textContent = t.g;
      root.classList.toggle('is-playing', playing);
      if (btnPlay) btnPlay.setAttribute('aria-label', (playing && cur === c) ? 'Pause' : 'Play');
    }

    function settle() {
      hits();
      select(center(pos));
      render();
      if (announce) announce.textContent = (playing ? 'Now playing: ' : 'Selected: ') + tracks[center(pos)].t;
    }

    function goToPos(p) {
      pos = snap(p);
      applyPos();
      render();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, reduce ? 30 : 420);
    }

    function moveToIndex(idx) {
      var cur0 = center(pos);
      var d = ((idx - cur0 + N + N / 2) % N) - N / 2; /* shortest way round, in covers */
      goToPos(pos + d / N);
    }

    function togglePlayCentre() {
      var c = center(pos);
      if (playing && cur === c) { pause(); return; }
      if (cur !== c || !audio) setTrack(c);
      play();
    }

    stage.addEventListener('pointerdown', function (e) {
      if (e.button) return;
      dragging = true; moved = false; sx = e.clientX; spos = pos;
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = sx - e.clientX;
      if (Math.abs(dx) > 6) moved = true;
      if (moved) { pos = spos + dx * 0.001; applyPos(); }
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      if (moved) { suppress = true; setTimeout(function () { suppress = false; }, 0); goToPos(pos); }
    }
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);
    stage.addEventListener('pointerleave', endDrag);

    stage.addEventListener('click', function (e) {
      if (suppress) return;
      var li = e.target.closest ? e.target.closest('.bcf__box') : null;
      if (!li) return;
      var idx = boxes.indexOf(li);
      if (idx < 0) return;
      if (idx === center(pos)) togglePlayCentre(); else moveToIndex(idx);
    });

    if (btnPrev) btnPrev.addEventListener('click', function () { goToPos(pos - 1 / N); });
    if (btnNext) btnNext.addEventListener('click', function () { goToPos(pos + 1 / N); });
    if (btnPlay) btnPlay.addEventListener('click', togglePlayCentre);

    root.addEventListener('keydown', function (e) {
      if (e.target && e.target.tagName === 'INPUT') return;
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') { e.preventDefault(); goToPos(pos - 1 / N); }
      if (e.code === 'ArrowRight' || e.code === 'KeyD') { e.preventDefault(); goToPos(pos + 1 / N); }
    });

    function progress() {
      if (!audio) return;
      var d = audio.duration;
      if (fillEl) fillEl.style.width = (isFinite(d) && d > 0 ? Math.min(100, (audio.currentTime / d) * 100) : 0) + '%';
      if (timeEl) timeEl.textContent = fmt(audio.currentTime);
      if (durEl) durEl.textContent = isFinite(d) ? fmt(d) : '0:00';
    }

    on(function (kind) {
      if (kind === 'time') { progress(); return; }
      if (kind === 'go' && center(pos) !== cur) moveToIndex(cur);
      render();
    });

    hits();
    render();
    return { goTo: goToPos, centre: function () { return center(pos); }, jump: function (i) { pos = snap(i / N); applyPos(); render(); hits(); } };
  }

  on(function (kind) { if (kind !== 'time') { renderToggles(); renderPill(); renderPairs(); } });

  function init() {
    var root = document.querySelector('[data-bcf]');
    if (root) flow = buildFlow(root);

    var st = readState();
    if (st && typeof st.i === 'number') {
      cur = ((st.i % N) + N) % N;
      if (flow) flow.jump(cur);
      if (st.on) { pendingSeek = st.t || 0; setTrack(cur); play(); }
    }
    renderToggles();
    renderPill();
    renderPairs();
  }

  window.addEventListener('pagehide', saveState);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
